import { contentLinkNodeNavigateTo } from 'src/modules/dashboard/helpers/contentLinkNodeNavigateTo';
import {
    ContentLinkFragment,
    ContentLink_InternalLinkNode_Fragment,
    ContentLink_MeditationLinkNode_Fragment,
} from '../documents/getHomeScreen.generated';
import { CareCardPropsWithId } from 'src/modules/care/components/CareCardRow';
import { filterNullish } from '@modernhealth/filter-nullish';
import i18n from 'src/config/i18n';
import { captureException } from 'src/shared/helpers/captureException';

const mapInternalLinkNodeToCareCardProps = (link: ContentLink_InternalLinkNode_Fragment) => {
    const filteredImages = filterNullish(link.images ?? []);
    if (!link.image && !link.providerInitials && !filteredImages.length) {
        captureException(
            new Error(
                'Expected one of image, providerInitials, or images to be valid for InternalLinkNode',
            ),
            {
                id: link.id,
            },
        );
    }
    return {
        id: link.id,
        title: link.title,
        description: link.subtitle ?? '',
        images: filteredImages.length > 0 ? (filteredImages as string[]) : undefined,
        label: link.labelText ?? undefined,
        variant: (link.labelVariant as CareCardPropsWithId['variant']) ?? 'default',
        isRecommended: link.recommended ?? false,
        iconUrl: link.image ?? undefined,
        initials: link.providerInitials ?? undefined,
        colorScheme: link.colorScheme ?? undefined,
        backgroundImage: link.backgroundImage ?? undefined,
        titleTranslationKey: link.titleTranslationKey ?? undefined,
        ...contentLinkNodeNavigateTo(link),
    };
};

const mapMeditationLinkNodeToCareCardProps = (link: ContentLink_MeditationLinkNode_Fragment) => {
    return link.meditation
        ? {
              id: link.id,
              title: link.meditation.name,
              description: i18n.t(
                  link.mediaType?.name
                      ? `shared:media_type.duration_with_min`
                      : 'dashboard:meditation.duration',
                  {
                      duration: link.meditation.duration ?? 1,
                      mediaTypeName: link.mediaType?.name,
                  },
              ),
              images: [link.banner?.imageUrl ?? link.meditation.image ?? ''],
              ...contentLinkNodeNavigateTo(link),
              // isRecommended: link.recommended ?? false,
          }
        : null;
};

export const mapContentLinkToCareCardProps = (
    link: ContentLinkFragment,
): CareCardPropsWithId | null => {
    switch (link.__typename) {
        case 'MeditationLinkNode':
            return null;
        case 'ProgramLinkNode':
            return null;
        case 'CircleSeriesLinkNode':
            return null;
        case 'ExternalLinkNode':
            return null;
        case 'InternalLinkNode':
            return mapInternalLinkNodeToCareCardProps(link);
        case 'CircleSessionLinkNode':
            return null;
        case 'CircleTopicLinkNode':
            return null;
        case 'ScreenLinkNode':
            return null;
        case 'CourseLinkNode':
            return null;
        default:
            return null;
    }
};

type MeditationLinkNodeToCareCardProps = Omit<CareCardPropsWithId, 'titleTranslationKey'>;

export const mapContentLinkToRecommendedNextStepProps = (
    link: ContentLinkFragment,
): CareCardPropsWithId | MeditationLinkNodeToCareCardProps | null => {
    switch (link.__typename) {
        case 'MeditationLinkNode':
            return mapMeditationLinkNodeToCareCardProps(link);
        case 'ProgramLinkNode':
            return null;
        case 'CircleSeriesLinkNode':
            return null;
        case 'ExternalLinkNode':
            return null;
        case 'InternalLinkNode':
            return mapInternalLinkNodeToCareCardProps(link);
        case 'CircleSessionLinkNode':
            return null;
        case 'CircleTopicLinkNode':
            return null;
        case 'ScreenLinkNode':
            return null;
        case 'CourseLinkNode':
            return null;
        default:
            return null;
    }
};
